import type { CustomerContact } from "../types";
import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/react";
import {
  Check,
  MoreVertical,
  Star,
  Pencil,
  Trash2,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ContactCardProps {
  contact: CustomerContact;
  onEdit: (contact: CustomerContact) => void;
  onDelete: (contact: CustomerContact) => void;
  onToggleFavorite: (contact: CustomerContact) => void;
  onSetDefault: (contact: CustomerContact) => void;
}

function labelClasses(label?: string | null): string {
  switch (label) {
    case "Office":
      return "bg-primary-50 text-primary-700";
    case "Home":
      return "bg-success-50 text-success-700";
    case "Warehouse":
      return "bg-default-100 text-default-700";
    default:
      return "bg-default-100 text-default-700";
  }
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function ContactCard({
  contact,
  onEdit,
  onDelete,
  onToggleFavorite,
  onSetDefault,
}: ContactCardProps) {
  const { t } = useTranslation("app");

  const formatAddress = () => {
    const parts = [contact.addressLine1];
    if (contact.addressLine2) parts.push(contact.addressLine2);
    parts.push(`${contact.addressCity}, ${contact.addressProvince}`);
    if (contact.addressPostalCode) parts.push(contact.addressPostalCode);
    return parts.join(", ");
  };

  return (
    <div
      className={cn(
        "bg-content1 rounded-large p-5 flex flex-col gap-3.5 shadow-small",
        contact.isDefault && "ring-2 ring-inset ring-primary shadow-medium"
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-2.5">
        <Avatar
          name={initials(contact.contactName)}
          size="md"
          classNames={{
            base: "bg-default-200 flex-shrink-0",
            name: "text-default-700 font-semibold",
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-[15px] font-semibold leading-tight">
              {contact.contactName}
            </div>
            {contact.isDefault && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                <Check className="h-2.5 w-2.5" />{" "}
                {t("addressBook.card.default")}
              </span>
            )}
            {contact.label && (
              <span
                className={cn(
                  "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold",
                  labelClasses(contact.label)
                )}
              >
                {contact.label}
              </span>
            )}
          </div>
          {contact.contactCompany && (
            <div className="text-xs text-default-500 mt-0.5 truncate">
              {contact.contactCompany}
            </div>
          )}
        </div>
        <button
          aria-label={
            contact.isFavorite
              ? t("addressBook.card.unfavorite")
              : t("addressBook.card.favorite")
          }
          className={cn(
            "w-7 h-7 grid place-items-center rounded-md transition-colors",
            contact.isFavorite
              ? "text-warning-500"
              : "text-default-300 hover:text-default-500"
          )}
          onClick={() => onToggleFavorite(contact)}
        >
          <Star
            className={cn("h-4 w-4", contact.isFavorite && "fill-current")}
          />
        </button>
        <Dropdown placement="bottom-end">
          <DropdownTrigger>
            <button
              aria-label={t("addressBook.card.actions")}
              className="w-7 h-7 grid place-items-center text-default-400 hover:text-default-700 rounded-md"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownTrigger>
          <DropdownMenu aria-label={t("addressBook.card.actions")}>
            <DropdownItem
              key="edit"
              startContent={<Pencil className="h-4 w-4" />}
              onPress={() => onEdit(contact)}
            >
              {t("addressBook.card.edit")}
            </DropdownItem>
            {!contact.isDefault ? (
              <DropdownItem
                key="set-default"
                startContent={<Check className="h-4 w-4" />}
                onPress={() => onSetDefault(contact)}
              >
                {t("addressBook.card.setAsDefault")}
              </DropdownItem>
            ) : null}
            <DropdownItem
              key="delete"
              color="danger"
              className="text-danger"
              startContent={<Trash2 className="h-4 w-4" />}
              onPress={() => onDelete(contact)}
            >
              {t("addressBook.card.delete")}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>

      {/* Address */}
      <div className="flex gap-2 text-[13px] text-default-700 leading-snug">
        <MapPin className="h-3.5 w-3.5 text-default-400 flex-shrink-0 mt-0.5" />
        <span>{formatAddress()}</span>
      </div>

      {/* Contact info */}
      {(contact.contactPhone ?? contact.contactEmail) && (
        <div className="flex flex-col gap-1.5 text-xs text-default-600">
          {contact.contactPhone && (
            <div className="flex items-center gap-2">
              <Phone className="h-3 w-3 text-default-400 flex-shrink-0" />
              <span>{contact.contactPhone}</span>
            </div>
          )}
          {contact.contactEmail && (
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="h-3 w-3 text-default-400 flex-shrink-0" />
              <span className="truncate">{contact.contactEmail}</span>
            </div>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="flex gap-2 pt-2.5 border-t border-divider">
        <Button
          variant="bordered"
          size="sm"
          startContent={<Pencil className="h-3.5 w-3.5" />}
          onPress={() => onEdit(contact)}
        >
          {t("addressBook.card.edit")}
        </Button>
        {!contact.isDefault && (
          <Button
            variant="light"
            size="sm"
            startContent={<Star className="h-3.5 w-3.5" />}
            onPress={() => onSetDefault(contact)}
          >
            {t("addressBook.card.setDefault")}
          </Button>
        )}
      </div>
    </div>
  );
}
